/**
 * FounderStepUp — Operation Lockdown UI.
 * Verification panel (passkey / authenticator code / recovery code) plus
 * enrollment (QR TOTP setup, passkey registration, recovery codes).
 */
import React, { useCallback, useEffect, useState } from "react";
import {
  Fingerprint, KeyRound, Loader2, ShieldCheck, ShieldAlert, RefreshCw,
  Copy, Trash2, Plus, QrCode,
} from "lucide-react";
import { toast } from "sonner";
import {
  stepupStatus, stepupTotpBegin, stepupTotpConfirm, stepupVerify,
  stepupRecoveryRegen, stepupWebauthnRegOptions, stepupWebauthnRegVerify,
  stepupWebauthnAuthOptions, stepupWebauthnAuthVerify, stepupPasskeyRemove,
} from "../../lib/api";
import { startRegistration, startAuthentication, webauthnSupported } from "../../lib/webauthnBrowser";
import { useCallsign } from "../../command/useCallsign";

const Mono = ({ children, className = "" }) => (
  <span className={`font-mono text-[9px] uppercase tracking-[0.25em] ${className}`}>{children}</span>
);

const RecoveryCodes = ({ codes }) => (
  <div className="border border-[#E0A526] bg-[#181205] p-3 mt-3" data-testid="stepup-recovery-codes">
    <Mono className="text-[#E0A526]">Recovery codes — shown once. Store offline.</Mono>
    <div className="grid grid-cols-2 gap-1 mt-2 font-mono text-[12px] text-[#E4DFD3]">
      {codes.map((c) => <span key={c}>{c}</span>)}
    </div>
    <button onClick={() => { navigator.clipboard?.writeText(codes.join("\n")); toast.success("Copied"); }}
            className="mt-2 inline-flex items-center gap-1 font-mono text-[9px] uppercase text-[#9FA8D4] underline"
            data-testid="stepup-copy-codes"><Copy size={10} /> Copy all</button>
  </div>
);

export const FounderStepUp = ({ opToken, userToken, onElevated }) => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState("");
  const [code, setCode] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  const [enroll, setEnroll] = useState(null);   // {qr_png, otpauth_uri, secret}
  const [newCodes, setNewCodes] = useState(null);

  const load = useCallback(async () => {
    try { setStatus(await stepupStatus(opToken, userToken)); }
    catch { setStatus(false); }
  }, [opToken, userToken]);
  useEffect(() => { load(); }, [load]);

  const finish = (r) => {
    if (r.recovery_codes) setNewCodes(r.recovery_codes);
    toast.success("Founder identity verified");
    load();
    if (!r.recovery_codes) onElevated?.();
  };

  const doPasskey = async () => {
    setBusy("passkey");
    try {
      const opts = await stepupWebauthnAuthOptions(opToken, userToken);
      const cred = await startAuthentication(opts);
      finish(await stepupWebauthnAuthVerify(opToken, cred, userToken));
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Passkey verification failed");
    } finally { setBusy(""); }
  };

  const doCode = async () => {
    if (!code.trim()) return;
    setBusy("code");
    try {
      finish(await stepupVerify(opToken, useRecovery ? "recovery" : "totp", code.trim(), userToken));
      setCode("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verification failed");
    } finally { setBusy(""); }
  };

  const beginTotp = async () => {
    setBusy("totp-begin");
    try { setEnroll(await stepupTotpBegin(opToken, userToken)); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not start enrollment"); }
    finally { setBusy(""); }
  };

  const confirmTotp = async () => {
    setBusy("totp-confirm");
    try {
      const r = await stepupTotpConfirm(opToken, code.trim(), userToken);
      setEnroll(null); setCode("");
      finish(r);
    } catch (e) { toast.error(e?.response?.data?.detail || "Invalid code"); }
    finally { setBusy(""); }
  };

  const registerPasskey = async () => {
    setBusy("passkey-reg");
    try {
      const opts = await stepupWebauthnRegOptions(opToken, userToken);
      const cred = await startRegistration(opts);
      const name = window.prompt("Name this passkey (e.g. iPhone Face ID, YubiKey 5)") || "Passkey";
      finish(await stepupWebauthnRegVerify(opToken, cred, name, userToken));
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Passkey registration failed");
    } finally { setBusy(""); }
  };

  if (status === null) {
    return <div className="py-8 text-center"><Loader2 size={16} className="animate-spin inline text-[#A68F5E]" /></div>;
  }
  if (status === false) return null;   // not the owner

  return (
    <div className="border border-[#D92D20]/50 bg-[#0d0a08] p-4" data-testid="founder-stepup-panel">
      <div className="flex items-center gap-2 mb-3">
        {status.elevated
          ? <ShieldCheck size={16} className="text-[#3FB950]" />
          : <ShieldAlert size={16} className="text-[#E0A526]" />}
        <Mono className="text-[#E4DFD3]">Founder Step-Up Authentication</Mono>
      </div>

      {newCodes && <RecoveryCodes codes={newCodes} />}
      {newCodes && (
        <button onClick={() => { setNewCodes(null); onElevated?.(); }}
                data-testid="stepup-codes-ack"
                className="mt-3 w-full min-h-[44px] bg-[#A68F5E] text-[#0a0907] font-mono text-[10px] uppercase tracking-[0.2em]">
          I have stored my recovery codes
        </button>
      )}

      {!newCodes && !status.enrolled && (
        <div data-testid="stepup-enroll">
          <p className="text-[12px] text-[#A39E93] leading-relaxed mb-3">
            No second factor is enrolled yet. Sensitive Founder actions will be
            locked behind step-up verification once you enroll a method below.
          </p>
          {!enroll ? (
            <div className="flex flex-wrap gap-2">
              {webauthnSupported() && (
                <button onClick={registerPasskey} disabled={!!busy} data-testid="stepup-register-passkey"
                        className="inline-flex items-center gap-2 min-h-[46px] px-3 border border-[#3FB950] text-[#3FB950] font-mono text-[10px] uppercase tracking-[0.18em]">
                  {busy === "passkey-reg" ? <Loader2 size={13} className="animate-spin" /> : <Fingerprint size={13} />}
                  Register Passkey (preferred)
                </button>
              )}
              <button onClick={beginTotp} disabled={!!busy} data-testid="stepup-begin-totp"
                      className="inline-flex items-center gap-2 min-h-[46px] px-3 border border-[#A68F5E] text-[#A68F5E] font-mono text-[10px] uppercase tracking-[0.18em]">
                {busy === "totp-begin" ? <Loader2 size={13} className="animate-spin" /> : <QrCode size={13} />}
                Set up Authenticator App
              </button>
            </div>
          ) : (
            <div>
              <img src={enroll.qr_png} alt="Scan with your authenticator app"
                   className="w-44 h-44 bg-white p-1" data-testid="stepup-totp-qr" />
              <p className="font-mono text-[9px] text-[#7B7168] mt-1 break-all">or enter: {enroll.secret}</p>
              <div className="flex gap-2 mt-2">
                <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="6-digit code"
                       inputMode="numeric" data-testid="stepup-totp-confirm-input"
                       className="w-36 bg-[#0F0E0B] border border-[#2D2A26] px-3 min-h-[46px] text-[16px] text-[#E4DFD3] tracking-[0.3em] focus:outline-none focus:border-[#A68F5E]" />
                <button onClick={confirmTotp} disabled={!!busy || code.trim().length < 6}
                        data-testid="stepup-totp-confirm-btn"
                        className="min-h-[46px] px-4 bg-[#A68F5E] text-[#0a0907] font-mono text-[10px] uppercase tracking-[0.2em] disabled:opacity-50">
                  {busy === "totp-confirm" ? <Loader2 size={13} className="animate-spin inline" /> : "Confirm"}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {!newCodes && status.enrolled && !status.elevated && (
        <div data-testid="stepup-verify">
          <p className="text-[12px] text-[#A39E93] leading-relaxed mb-3">
            Verify your identity to unlock sensitive Founder actions for 15 minutes.
          </p>
          <div className="space-y-2">
            {status.passkeys?.length > 0 && webauthnSupported() && (
              <button onClick={doPasskey} disabled={!!busy} data-testid="stepup-verify-passkey"
                      className="w-full inline-flex items-center justify-center gap-2 min-h-[50px] border border-[#3FB950] text-[#3FB950] font-mono text-[11px] uppercase tracking-[0.2em]">
                {busy === "passkey" ? <Loader2 size={14} className="animate-spin" /> : <Fingerprint size={14} />}
                Verify with Passkey
              </button>
            )}
            {status.totp_enabled && (
              <div className="flex gap-2">
                <input value={code} onChange={(e) => setCode(e.target.value)}
                       placeholder={useRecovery ? "Recovery code" : "Authenticator code"}
                       data-testid="stepup-code-input"
                       className="flex-1 bg-[#0F0E0B] border border-[#2D2A26] px-3 min-h-[48px] text-[16px] text-[#E4DFD3] tracking-[0.2em] focus:outline-none focus:border-[#A68F5E]" />
                <button onClick={doCode} disabled={!!busy || !code.trim()} data-testid="stepup-code-verify"
                        className="min-h-[48px] px-4 bg-[#A68F5E] text-[#0a0907] font-mono text-[10px] uppercase tracking-[0.2em] disabled:opacity-50">
                  {busy === "code" ? <Loader2 size={13} className="animate-spin inline" /> : "Verify"}
                </button>
              </div>
            )}
            <button onClick={() => setUseRecovery((v) => !v)} data-testid="stepup-toggle-recovery"
                    className="font-mono text-[9px] uppercase tracking-[0.15em] text-[#7B7168] underline underline-offset-2">
              {useRecovery ? "Use authenticator code" : `Use a recovery code (${status.recovery_codes_left} left)`}
            </button>
          </div>
        </div>
      )}

      {!newCodes && status.enrolled && status.elevated && (
        <div data-testid="stepup-manage">
          <div className="space-y-1.5">
            {(status.passkeys || []).map((p) => (
              <div key={p.id} className="flex items-center gap-2 bg-[#0F0E0B] border border-[#2D2A26] px-2 py-2">
                <KeyRound size={11} className="text-[#3FB950]" />
                <span className="font-mono text-[10px] text-[#E4DFD3]">{p.name}</span>
                <button onClick={async () => {
                          try { await stepupPasskeyRemove(opToken, p.id, userToken); load(); }
                          catch (e) { toast.error(e?.response?.data?.detail || "Remove failed"); }
                        }}
                        data-testid={`stepup-remove-passkey-${p.id}`}
                        className="ml-auto text-[#7B7168] hover:text-[#D92D20]"><Trash2 size={12} /></button>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-2 mt-2">
            {webauthnSupported() && (
              <button onClick={registerPasskey} disabled={!!busy} data-testid="stepup-add-passkey"
                      className="inline-flex items-center gap-1.5 min-h-[40px] px-2.5 border border-[#454038] text-[#C9C2B4] font-mono text-[9px] uppercase tracking-[0.15em]">
                <Plus size={11} /> Add passkey
              </button>
            )}
            <button onClick={beginTotp} disabled={!!busy} data-testid="stepup-rotate-totp"
                    className="inline-flex items-center gap-1.5 min-h-[40px] px-2.5 border border-[#454038] text-[#C9C2B4] font-mono text-[9px] uppercase tracking-[0.15em]">
              <QrCode size={11} /> {status.totp_enabled ? "Rotate authenticator" : "Add authenticator"}
            </button>
            <button onClick={async () => {
                      setBusy("regen");
                      try { setNewCodes((await stepupRecoveryRegen(opToken, userToken)).recovery_codes); }
                      catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
                      finally { setBusy(""); }
                    }}
                    disabled={!!busy} data-testid="stepup-regen-codes"
                    className="inline-flex items-center gap-1.5 min-h-[40px] px-2.5 border border-[#454038] text-[#C9C2B4] font-mono text-[9px] uppercase tracking-[0.15em]">
              <RefreshCw size={11} /> New recovery codes
            </button>
          </div>
          {enroll && (
            <div className="mt-3">
              <img src={enroll.qr_png} alt="Scan with your authenticator app" className="w-44 h-44 bg-white p-1" />
              <div className="flex gap-2 mt-2">
                <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="6-digit code"
                       inputMode="numeric"
                       className="w-36 bg-[#0F0E0B] border border-[#2D2A26] px-3 min-h-[46px] text-[16px] text-[#E4DFD3] tracking-[0.3em]" />
                <button onClick={confirmTotp} disabled={!!busy || code.trim().length < 6}
                        className="min-h-[46px] px-4 bg-[#A68F5E] text-[#0a0907] font-mono text-[10px] uppercase disabled:opacity-50">Confirm</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* Green status banner for the Security Center header. */
export const StepUpBanner = ({ status, onVerifyAgain }) => {
  const identity = useCallsign();
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 30_000);
    return () => clearInterval(id);
  }, []);
  if (!status?.elevated) return null;
  const remaining = Math.max(0, Math.round((new Date(status.expires_at) - Date.now()) / 60000));
  return (
    <div className="flex flex-wrap items-center gap-3 border border-[#3FB950]/40 bg-[#07120a] px-3 py-2 mb-4"
         data-testid="stepup-banner">
      <ShieldCheck size={14} className="text-[#3FB950]" />
      <Mono className="text-[#3FB950]">
        🟢 Founder Identity Verified{identity?.callsign ? ` — ${identity.callsign}` : ""}
      </Mono>
      <Mono className="text-[#7B7168]">
        Method: {status.method === "passkey" ? "Passkey" : status.method === "recovery" ? "Recovery code" : "Authenticator"}
      </Mono>
      <Mono className="text-[#7B7168]">Verified {new Date(status.verified_at).toLocaleTimeString()}</Mono>
      <Mono className="text-[#7B7168]">{remaining} min remaining</Mono>
      <button onClick={onVerifyAgain} data-testid="stepup-verify-again"
              className="ml-auto font-mono text-[9px] uppercase tracking-[0.15em] text-[#9FA8D4] underline underline-offset-2 min-h-[36px]">
        Verify Again
      </button>
    </div>
  );
};

export default FounderStepUp;
